import type { ReactNode } from 'react';
import { Link, useMatches } from '@tanstack/react-router';
import { CaptureAperture } from '@kapture/ui';
import { cn } from '@/lib/cn';
import { InstallPrompt } from './InstallPrompt';
import { OnlineIndicator } from './OnlineIndicator';
import { Toasts } from './Toasts';

interface AppShellProps {
  children: ReactNode;
}

/**
 * AppShell renders the chrome (header + nav + sync indicator) around the
 * matched leaf route. Chrome mode is driven by the leaf route's
 * `staticData` (declared in routes/__root.tsx):
 *
 *   - `fullscreen: true` → no chrome, full viewport. Used for HUD-like
 *     pages (mode picker, camera, note editor).
 *   - `mobile: true` → compact 480px-wide mobile chrome.
 *   - (default) → desktop-ish chrome with nav links + wider column.
 */
export function AppShell({ children }: AppShellProps) {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  const mobile = Boolean(leaf?.staticData.mobile);
  const fullscreen = Boolean(leaf?.staticData.fullscreen);

  if (fullscreen) {
    return (
      <div className="bg-graphite text-hud-ink relative flex min-h-dvh flex-col">
        {children}
        <Toasts />
      </div>
    );
  }

  if (mobile) {
    return (
      <div className="bg-surface-2 flex min-h-dvh flex-col">
        <header className="border-stroke bg-surface-2/95 sticky top-0 z-30 border-b backdrop-blur">
          <div className="mx-auto flex max-w-[480px] items-center justify-between px-4 py-2.5">
            <Brand />
            <div className="ml-auto">
              <OnlineIndicator />
            </div>
          </div>
        </header>
        <InstallPrompt />
        <main className="relative mx-auto flex w-full max-w-[480px] flex-1 flex-col">
          {children}
        </main>
        <Toasts />
      </div>
    );
  }

  return (
    <div className="bg-surface-2 flex min-h-dvh flex-col">
      <header className="border-stroke bg-surface-2/80 sticky top-0 z-30 border-b backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3">
          <Brand />
          <nav className="ml-4 flex items-center gap-1">
            <ShellLink to="/">Sessions</ShellLink>
            <ShellLink to="/welcome">Welcome</ShellLink>
            <ShellLink to="/design-showcase">Design</ShellLink>
          </nav>
          <div className="ml-auto">
            <OnlineIndicator />
          </div>
        </div>
      </header>
      <InstallPrompt />
      <main className="flex-1">{children}</main>
      <Toasts />
    </div>
  );
}

function Brand() {
  return (
    <span className="inline-flex select-none items-center gap-2" role="img" aria-label="Kapture AI">
      <CaptureAperture size={24} state="active" />
      <span className="text-ink font-sans text-[13.5px] font-bold tracking-tight">Kapture AI</span>
    </span>
  );
}

function ShellLink({
  to,
  children,
}: {
  to: '/' | '/welcome' | '/design-showcase';
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      activeOptions={{ exact: true }}
      className={cn(
        'rounded-item px-3 py-1.5 text-sm font-medium transition-colors',
        'text-text-dim hover:bg-surface-3 hover:text-ink',
      )}
      // The active indicator is the flame — one of the four jobs the accent
      // exists for. It was ink, which left the shell with no brand moment and
      // read as a disabled pill rather than a selected one.
      activeProps={{
        className: 'bg-brand text-brand-ink hover:bg-brand hover:text-brand-ink',
      }}
    >
      {children}
    </Link>
  );
}
